/**
 * The engine clock for a live run: wall time (performance.now()) minus the
 * time spent paused, starting at 0 when the run starts.
 *
 * The server replays runs from engine time 0 (see core engine.ts, "Clock
 * contract"), so the host must never feed wall-clock timestamps to the engine
 * and must stop the clock while paused — otherwise pausing would eat into the
 * falling block's time.
 *
 * Pure values: every operation returns a new clock.
 */

export interface PauseClock {
  /** Wall time (ms) at which engine time was 0. */
  readonly origin: number;
  /** Total wall time spent paused before the current pause (ms). */
  readonly pausedTotal: number;
  /** Wall time the current pause began, or null while running. */
  readonly pausedAt: number | null;
}

/** A running clock whose engine time is 0 at `wallNow`. */
export function startClock(wallNow: number): PauseClock {
  return { origin: wallNow, pausedTotal: 0, pausedAt: null };
}

export function isClockPaused(clock: PauseClock): boolean {
  return clock.pausedAt !== null;
}

/** Engine time at `wallNow`: frozen while paused, never negative. */
export function engineTime(clock: PauseClock, wallNow: number): number {
  const wall = clock.pausedAt ?? wallNow;
  return Math.max(0, wall - clock.origin - clock.pausedTotal);
}

/** Stop the clock at `wallNow`. Pausing a paused clock changes nothing. */
export function pauseClock(clock: PauseClock, wallNow: number): PauseClock {
  if (clock.pausedAt !== null) return clock;
  return { ...clock, pausedAt: wallNow };
}

/** Restart the clock at `wallNow`; the paused stretch is excluded from engine time. */
export function resumeClock(clock: PauseClock, wallNow: number): PauseClock {
  if (clock.pausedAt === null) return clock;
  const pausedFor = Math.max(0, wallNow - clock.pausedAt);
  return { ...clock, pausedTotal: clock.pausedTotal + pausedFor, pausedAt: null };
}
